import { useState } from 'react';
import { api } from '../api';
import { getOrgSlug } from '../utils/getOrgSlug';
import { getErrorMessage } from '../../../shared/src';

interface ForgotPasswordPageProps {
  onBack: () => void;
}

export function ForgotPasswordPage({ onBack }: ForgotPasswordPageProps) {
  const orgSlug = getOrgSlug();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await api.requestPasswordReset(email, orgSlug);
      setSuccess(true);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Check Your Email</h1>
          <p className="subtitle">
            If an account exists for <strong>{email}</strong>, you will receive
            a password reset link shortly.
          </p>
          <button className="btn-primary btn-full" onClick={onBack}>
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Reset Password</h1>
        <p className="subtitle">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@company.com"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button
            type="submit"
            className="btn-primary btn-full"
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <button className="btn-link back-link" onClick={onBack}>
          &larr; Back to Sign In
        </button>
      </div>

      <style>{`
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
        }

        .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4a90a4;
          box-shadow: 0 0 0 2px rgba(74, 144, 164, 0.1);
        }

        .error-message {
          margin-bottom: 16px;
          padding: 10px;
          background-color: #f8d7da;
          border-radius: 4px;
          color: #721c24;
          font-size: 14px;
        }

        .back-link {
          display: block;
          margin-top: 20px;
          color: #4a90a4;
          text-decoration: none;
          font-size: 14px;
          background: none;
          border: none;
          cursor: pointer;
        }

        .back-link:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
